import AppComponent from '../base/app-component.ts';
import { hasPerm } from '../lib/perm.ts';
import { templateParse, templateParseToElement } from '../lib/template-parse.ts';
import { etichettaStato } from '@/types/entities/delega.ts';
import { etichettaEsito } from '@/types/entities/coda.ts';

// --- Tipi ---

export interface ColumnDef {
  key: string;
  label: string;
  type: 'text' | 'code' | 'badge' | 'date' | 'link' | 'periodo' | 'numero' | 'html';
  sortable?: boolean;
  filterable?: boolean;
  badgeClass?: string;
  badgeFilter?: string;
  badgeTextFilter?: string;
  href?: string;
  text?: string;
  emptyText?: string;
  truncate?: number;
  cssClass?: string;
}

export interface RowAction {
  name: string;
  icon: string;
  btnClass?: string;
  title?: string;
  label?: string;
  visibleIf?: { field: string; value: string };
  perm?: string;
}

interface PaginationInfo {
  page: number;
  totalPages: number;
  count: number;
  pageSize: number;
}

interface TableState {
  page: number;
  sort: string | null;
  sortDir: 'asc' | 'desc' | null;
  filter: string;
}

// Mappe filtri badge (allineate a template-parse.ts)
const BADGE_FILTERS: Record<string, Record<string, string>> = {
  stato_badge: {
    CONFERMATA: 'success', DA_PRESENTARE: 'warning', PRESENTATA: 'info', RIFIUTATA: 'danger',
    V: 'success', T: 'warning', C: 'danger', D: 'dark',
  },
  stato_delega_badge: {
    BOZZA: 'secondary', DA_PRESENTARE: 'info', PRESENTATA: 'primary',
    CONFERMATA: 'success', ARCHIVIATA: 'danger',
  },
  badge_cassa: {
    CE: 'primary', EC: 'warning',
  },
  esito_presentazione_badge: {
    CONFERMATA: 'success', SCARTATA: 'danger', IN_ATTESA: 'secondary',
  },
  sindacato_badge: {
    '20': 'success', '22': 'success', '10': 'danger', '32': 'danger', '30': 'danger', '9': 'secondary',
  },
};

const TEXT_FILTERS: Record<string, (v: string) => string> = {
  stato_label: etichettaStato,
  nome_sindacato: (v) => {
    const map: Record<string, string> = { '9': 'NON ISCRITTI', '10': 'FILLEA', '20': 'FILCA', '22': 'FILCA', '30': 'FENEAL', '32': 'FILLEA' };
    return map[v] ?? v;
  },
  esito_presentazione_label: etichettaEsito,
};

// --- Componente ---

export class DataTable extends AppComponent {
  static override componentName = 'wc-data-table';

  protected override get templateId() { return 'data-table'; }

  // Stato interno
  private columns: ColumnDef[] = [];
  private actions: RowAction[] = [];
  private rows: Record<string, unknown>[] = [];
  private pagination: PaginationInfo | null = null;
  private currentPage = 1;
  private currentSort: string | null = null;
  private currentDir: 'asc' | 'desc' | null = null;
  private filterText = '';

  // --- Lifecycle ---

  protected override buildRenderingData(): Record<string, unknown> {
    return {
      ...super.buildRenderingData(),
      showFilter: this.attributeBoolean('show-filter'),
      filterPlaceholder: this.attributeString('filter-placeholder') || 'Filtra...',
      showPagination: this.attributeBoolean('show-pagination', true),
      actionsType: this.attributeString('actions-type'),
    };
  }

  protected override afterRender(): void {
    this.columns = this.parseColumns();
    this.actions = this.parseActions();

    this.setupSort();
    this.setupPagination();
    this.setupRowActions();
    if (this.attributeBoolean('show-filter')) this.setupFilter();
    if (this.attributeBoolean('row-clickable')) this.setupRowClick();
  }

  // --- Metodi pubblici ---

  setData(rows: Record<string, unknown>[], pagination?: PaginationInfo): void {
    this.rows = rows;
    this.pagination = pagination ?? null;
    if (pagination) this.currentPage = pagination.page;
    this.renderTable();
  }

  setLoading(): void {
    const container = this.$('[data-dt-content]');
    if (container) templateParseToElement('shared-loading-state', container, {});
    const pag = this.$('[data-dt-pagination]');
    if (pag) pag.innerHTML = '';
  }

  setError(message: string): void {
    const container = this.$('[data-dt-content]');
    if (container) templateParseToElement('shared-error-state', container, { message });
    const pag = this.$('[data-dt-pagination]');
    if (pag) pag.innerHTML = '';
  }

  getState(): TableState {
    return {
      page: this.currentPage,
      sort: this.currentSort,
      sortDir: this.currentDir,
      filter: this.filterText,
    };
  }

  // --- Setup eventi ---

  private setupSort(): void {
    this.delegate.on('click', '[data-dt-sort]', (e: Event) => {
      const th = (e.target as HTMLElement).closest('[data-dt-sort]') as HTMLElement;
      const key = th.dataset.dtSort!;
      if (this.currentSort === key) {
        if (this.currentDir === 'asc') {
          this.currentDir = 'desc';
        } else {
          this.currentSort = null;
          this.currentDir = null;
        }
      } else {
        this.currentSort = key;
        this.currentDir = 'asc';
      }
      this.currentPage = 1;
      this.updateHead();
      this.dispatch('dt-sort-change', { sort: this.currentSort, dir: this.currentDir });
    });
  }

  private updateHead(): void {
    const oldThead = this.querySelector('[data-dt-content] thead');
    if (!oldThead) return;
    const tmp = document.createElement('table');
    tmp.innerHTML = this.renderHead();
    const newThead = tmp.querySelector('thead');
    if (newThead) oldThead.replaceWith(newThead);
  }

  private setupPagination(): void {
    this.delegate.on('click', '[data-dt-page="prev"]', () => {
      if (this.currentPage > 1) {
        this.currentPage--;
        this.dispatch('dt-page-change', { page: this.currentPage });
      }
    });
    this.delegate.on('click', '[data-dt-page="next"]', () => {
      if (this.pagination && this.currentPage < this.pagination.totalPages) {
        this.currentPage++;
        this.dispatch('dt-page-change', { page: this.currentPage });
      }
    });
  }

  private setupFilter(): void {
    const input = this.$('[data-dt-filter-input]') as HTMLInputElement | null;
    if (!input) return;

    let debounceTimer: ReturnType<typeof setTimeout> | null = null;

    this.listen(input, 'input', () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        this.filterText = input.value.trim();
        this.currentPage = 1;
        this.dispatch('dt-filter-change', { filter: this.filterText });
      }, 300);
    });

    this.listen(input, 'search', () => {
      if (input.value === '') {
        if (debounceTimer) clearTimeout(debounceTimer);
        this.filterText = '';
        this.currentPage = 1;
        this.dispatch('dt-filter-change', { filter: '' });
      }
    });
  }

  private setupRowClick(): void {
    this.delegate.on('click', '[data-dt-row]', (e: Event) => {
      const originalTarget = (e as any).originalTarget as HTMLElement;
      if (originalTarget && (originalTarget as HTMLElement).closest('[data-dt-action]')) return;
      if (originalTarget && (originalTarget as HTMLElement).closest('a')) return;
      const tr = (e.target as HTMLElement).closest('[data-dt-row]') as HTMLElement;
      const index = Number(tr.dataset.dtRow);
      this.dispatch('dt-row-click', { row: this.rows[index], index });
    });
  }

  private setupRowActions(): void {
    this.delegate.on('click', '[data-dt-action]', (e: Event) => {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      const btn = (e.target as HTMLElement).closest('[data-dt-action]') as HTMLElement;
      const action = btn.dataset.dtAction!;
      // Il body standard porta l'indice su [data-dt-actions], i body custom su [data-dt-row]
      const holder = btn.closest('[data-dt-actions], [data-dt-row]') as HTMLElement | null;
      if (!holder) return;
      const index = Number(holder.dataset.dtActions ?? holder.dataset.dtRow);
      this.dispatch('dt-row-action', { action, row: this.rows[index], index });
    });
  }

  // --- Rendering ---

  private renderTable(): void {
    const container = this.$('[data-dt-content]');
    if (!container) return;
    if (this.rows.length === 0) {
      templateParseToElement('shared-empty-state', container, {
        icon: this.attributeString('empty-icon', 'inbox'),
        message: this.attributeString('empty-message', 'Nessun elemento trovato'),
      });
      this.renderPagination(null);
      return;
    }

    const preparedRows = this.rows.map(row => this.prepareRow(row));
    const rowTemplateId = this.attributeString('row-template-id');
    const headHtml = this.renderHead();
    const bodyHtml = rowTemplateId
      ? this.renderCustomBody(rowTemplateId, preparedRows)
      : this.renderStandardBody(preparedRows);

    container.innerHTML =
      '<table class="table table-sm table-hover align-middle mb-0">' +
      headHtml + bodyHtml +
      '</table>';

    this.renderPagination(this.pagination);
    this.dispatch('dt-render-complete', { rows: this.rows, count: this.pagination?.count ?? this.rows.length });
  }

  private renderHead(): string {
    const data = {
      columns: this.columns.map(c => ({
        ...c,
        cssClass: this.buildThClass(c),
      })),
      currentSort: this.currentSort,
      currentDir: this.currentDir,
      actions: this.actions,
    };
    return templateParse('data-table-head', data) ?? '';
  }

  private renderStandardBody(preparedRows: Record<string, unknown>[]): string {
    return templateParse('data-table-body', {
      rows: preparedRows,
      columns: this.columns,
      actions: this.actions,
      rowClickable: this.attributeBoolean('row-clickable'),
      actionsType: this.attributeString('actions-type'),
    }) ?? '';
  }

  private renderCustomBody(templateId: string, preparedRows: Record<string, unknown>[]): string {
    return templateParse(templateId, {
      rows: preparedRows.map(r => r.__raw),
      columns: this.columns,
      actions: this.actions,
    }) ?? '';
  }

  private renderPagination(pagination: PaginationInfo | null): void {
    const container = this.$('[data-dt-pagination]');
    if (!container) return;
    if (!pagination || pagination.totalPages <= 1) {
      if (pagination && pagination.count > 0) {
        const label = pagination.count === 1 ? '1 elemento' : `${pagination.count} elementi`;
        container.innerHTML = `<div class="text-muted small mt-2">${label}</div>`;
      } else {
        container.innerHTML = '';
      }
      return;
    }
    const rangeStart = (pagination.page - 1) * pagination.pageSize + 1;
    const rangeEnd = Math.min(pagination.page * pagination.pageSize, pagination.count);
    templateParseToElement('data-table-pagination', container, {
      ...pagination,
      rangeStart,
      rangeEnd,
    });
  }

  // --- Preparazione dati ---

  private prepareRow(row: Record<string, unknown>): Record<string, unknown> {
    const flat: Record<string, unknown> = { __raw: row };

    for (const col of this.columns) {
      const value = this.resolveValue(row, col.key);
      flat[col.key] = value;

      if (col.type === 'badge' && value != null) {
        if (col.badgeFilter && BADGE_FILTERS[col.badgeFilter]) {
          flat[col.key + '__badgeClass'] = BADGE_FILTERS[col.badgeFilter]![String(value)] ?? 'secondary';
        } else {
          flat[col.key + '__badgeClass'] = col.badgeClass ?? 'secondary';
        }
        if (col.badgeTextFilter && TEXT_FILTERS[col.badgeTextFilter]) {
          flat[col.key + '__badgeText'] = TEXT_FILTERS[col.badgeTextFilter]!(String(value));
        } else {
          flat[col.key + '__badgeText'] = value;
        }
      }

      if (col.type === 'link') {
        flat[col.key + '__href'] = col.href ? this.resolvePlaceholders(col.href, row) : '#';
        flat[col.key + '__text'] = col.text ? this.resolvePlaceholders(col.text, row) : (value ?? '');
      }
    }

    // Filtra azioni con visibleIf in base ai dati della riga
    const visibleActions = this.actions.filter(a => {
      if (a.perm && !hasPerm(a.perm)) return false;
      if (!a.visibleIf) return true;
      return String(this.resolveValue(row, a.visibleIf.field)) === a.visibleIf.value;
    });
    flat.__visibleActions = visibleActions;

    return flat;
  }

  private resolveValue(obj: Record<string, unknown>, key: string): unknown {
    return key.split('.').reduce<unknown>((current, part) => {
      if (current != null && typeof current === 'object') {
        return (current as Record<string, unknown>)[part];
      }
      return undefined;
    }, obj);
  }

  private resolvePlaceholders(template: string, row: Record<string, unknown>): string {
    return template.replace(/\{\{(\w[\w.]*)\}\}/g, (_, key: string) => {
      const val = this.resolveValue(row, key);
      return val != null ? String(val) : '';
    });
  }

  // --- Utility ---

  private parseColumns(): ColumnDef[] {
    const raw = this.getAttribute('data-columns');
    if (!raw) return [];
    try { return JSON.parse(raw) as ColumnDef[]; }
    catch { console.warn('[wc-data-table] data-columns non e\' JSON valido'); return []; }
  }

  private parseActions(): RowAction[] {
    const raw = this.getAttribute('data-actions');
    if (!raw) return [];
    try { return JSON.parse(raw) as RowAction[]; }
    catch { return []; }
  }

  private buildThClass(col: ColumnDef): string {
    const parts: string[] = [];
    if (col.cssClass) parts.push(col.cssClass);
    if (col.sortable) parts.push('user-select-none');
    return parts.join(' ');
  }
}